import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Star, Crown, CheckCircle2, Award, Eye, Heart, Flame } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { directory } from '@/lib/directory';
import { useAuth } from '@/contexts/AuthContext';
import { TYPE_META } from '@/lib/seo';

const catLabel = (b) => (TYPE_META[b.type]?.label) || (b.category || b.type || '').replace(/-/g, ' ');
const bizUrl = (b) => `/business/${b.slug || b.id}`;

// A single, real (DB-backed) business card used by Trending + Top-Rated showcases.
export const BusinessShowcaseCard = ({ b, trendLabel, initialSaved = false }) => {
  const { user } = useAuth();
  const [saved, setSaved] = useState(initialSaved);
  const [busy, setBusy] = useState(false);

  const toggleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user || !user.user_id) { toast.error('Sign in to save places'); return; }
    if (busy) return;
    setBusy(true);
    const next = !saved;
    setSaved(next);
    try {
      if (next) { await directory.addFavorite(b.id); toast.success(`Saved ${b.name}`); }
      else { await directory.removeFavorite(b.id); toast.success(`Removed ${b.name}`); }
    } catch (_) {
      setSaved(!next);
      toast.error('Could not update saved places');
    } finally { setBusy(false); }
  };

  return (
    <article
      className="group relative w-72 sm:w-80 shrink-0 rounded-2xl overflow-hidden bg-white border border-gray-200 shadow hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 flex flex-col"
      data-testid={`business-card-${b.id}`}
    >
      <Link to={bizUrl(b)} className="block focus:outline-none">
        <div className="relative h-44 overflow-hidden">
          <img src={b.image} alt={b.name} loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
          {trendLabel && (
            <Badge className="absolute top-3 left-3 bg-[#D32323] text-white border-0 shadow">
              <Flame className="w-3 h-3 mr-1" /> {trendLabel}
            </Badge>
          )}
          <div className="absolute top-3 right-3 flex flex-col gap-1.5 items-end">
            {b.premium && (
              <Badge className="bg-gradient-to-r from-amber-500 to-amber-600 text-white border-0 shadow-md" data-testid={`card-premium-${b.id}`}>
                <Crown className="w-3 h-3 mr-1" /> Premium
              </Badge>
            )}
            {b.verified && (
              <Badge className="bg-[#00AA6C] text-white border-0 shadow" data-testid={`card-verified-${b.id}`}>
                <CheckCircle2 className="w-3 h-3 mr-1" /> Verified
              </Badge>
            )}
            {b.featured && !b.premium && (
              <Badge className="bg-white/95 text-[#D32323] border-0 shadow">
                <Award className="w-3 h-3 mr-1" /> Featured
              </Badge>
            )}
          </div>
          <div className="absolute bottom-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/95 backdrop-blur text-xs font-semibold shadow">
            <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" /> {b.rating}
            {b.reviews_count != null && <span className="text-gray-500 font-normal">({b.reviews_count})</span>}
          </div>
        </div>
        <div className="p-4">
          <h3 className="font-heading text-base font-semibold text-gray-900 line-clamp-1 group-hover:text-[#00AA6C] transition-colors">{b.name}</h3>
          <div className="flex items-center justify-between mt-1.5 text-xs">
            <Badge variant="outline" className="capitalize text-[10px] font-semibold">{catLabel(b)}</Badge>
            <span className="flex items-center gap-1 text-gray-500 truncate ml-2">
              <MapPin className="w-3 h-3 shrink-0" />
              <span className="truncate capitalize">{(b.city || '').replace(/-/g, ' ')}</span>
            </span>
          </div>
        </div>
      </Link>
      <div className="flex items-stretch border-t border-gray-100 mt-auto">
        <button type="button" onClick={toggleSave}
          className="flex-1 flex items-center justify-center gap-1 py-2.5 text-xs font-medium text-gray-700 hover:text-[#D32323] hover:bg-red-50 transition-colors"
          aria-label={`Save ${b.name}`} data-testid={`card-save-${b.id}`}>
          <Heart className={`w-3.5 h-3.5 ${saved ? 'fill-red-500 text-red-500' : ''}`} />
          {saved ? 'Saved' : 'Save'}
        </button>
        <Link to={bizUrl(b)}
          className="flex-1 flex items-center justify-center gap-1 py-2.5 text-xs font-semibold text-white bg-[#00AA6C] hover:bg-[#008F5A] transition-colors"
          data-testid={`card-quickview-${b.id}`}>
          <Eye className="w-3.5 h-3.5" /> Quick view
        </Link>
      </div>
    </article>
  );
};
